export function noticeTotalMinutes(
  days: number | null | undefined,
  hours: number | null | undefined,
  minutes: number | null | undefined,
): number {
  const d = days ?? 0;
  const h = hours ?? 0;
  const m = minutes ?? 0;
  return d * 24 * 60 + h * 60 + m;
}

/**
 * Converts days, hours, and minutes to TimeSpan format (D.HH:mm:ss)
 * Automatically normalizes (e.g., 45 hours → 1 day and 21 hours)
 * @param days - Number of days
 * @param hours - Number of hours
 * @param minutes - Number of minutes
 * @returns TimeSpan formatted string (D.HH:mm:ss) or null if all values are 0
 */
export function daysHoursMinutesToTimeSpan(
  days: number | null | undefined,
  hours: number | null | undefined,
  minutes: number | null | undefined,
): string | null {
  const daysValue = days ?? 0;
  const hoursValue = hours ?? 0;
  const minutesValue = minutes ?? 0;

  if (daysValue === 0 && hoursValue === 0 && minutesValue === 0) {
    return null;
  }

  // Convert everything to total minutes first
  const totalMinutes = daysValue * 24 * 60 + hoursValue * 60 + minutesValue;

  if (totalMinutes < 0) {
    return null;
  }

  // Normalize: convert to days, hours, minutes, seconds
  const normalizedDays = Math.floor(totalMinutes / (24 * 60));
  const remainingMinutesAfterDays = totalMinutes % (24 * 60);
  const normalizedHours = Math.floor(remainingMinutesAfterDays / 60);
  const normalizedMinutes = remainingMinutesAfterDays % 60;
  const secs = 0;

  return `${normalizedDays}.${String(normalizedHours).padStart(2, "0")}:${String(normalizedMinutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

/**
 * Converts TimeSpan format (D.HH:mm:ss or HH:mm:ss) to days, hours, and minutes
 * @param timeSpan - TimeSpan formatted string
 * @returns Object with days, hours, and minutes, or null if invalid
 */
export function timeSpanToDaysHoursMinutes(
  timeSpan: string | null | undefined,
): { days: number; hours: number; minutes: number } | null {
  if (!timeSpan || typeof timeSpan !== "string") {
    return { days: 0, hours: 0, minutes: 0 };
  }

  const trimmed = timeSpan.trim();
  if (trimmed === "") {
    return { days: 0, hours: 0, minutes: 0 };
  }

  let days = 0;
  let hours = 0;
  let minutes = 0;

  // Handle D.HH:mm:ss format (e.g., "1.02:30:00" = 1 day, 2 hours, 30 minutes)
  const timeSpanRegex = /^(\d+)\.(\d{2}):(\d{2}):(\d{2})$/;
  const timeSpanMatch = trimmed.match(timeSpanRegex);
  if (timeSpanMatch) {
    days = parseInt(timeSpanMatch[1], 10);
    hours = parseInt(timeSpanMatch[2], 10);
    minutes = parseInt(timeSpanMatch[3], 10);
  } else {
    // Handle HH:mm:ss format (e.g., "02:30:00" = 2 hours, 30 minutes)
    const timeRegex = /^(\d{1,2}):(\d{2}):(\d{2})$/;
    const timeMatch = trimmed.match(timeRegex);
    if (timeMatch) {
      hours = parseInt(timeMatch[1], 10);
      minutes = parseInt(timeMatch[2], 10);
    } else {
      // Handle HH:mm format (e.g., "02:30" = 2 hours, 30 minutes)
      const timeShortRegex = /^(\d{1,2}):(\d{2})$/;
      const timeShortMatch = trimmed.match(timeShortRegex);
      if (timeShortMatch) {
        hours = parseInt(timeShortMatch[1], 10);
        minutes = parseInt(timeShortMatch[2], 10);
      } else {
        return { days: 0, hours: 0, minutes: 0 };
      }
    }
  }

  return { days, hours, minutes };
}


